import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardContent,
    IonButton,
    IonLabel,
    IonItem,
    IonRadioGroup,
    IonRadio,
    IonModal,
} from "@ionic/react";


interface QuestionData {
    word: string;
    correctAnswer: string;
    options: string[]; // Type of noun: Concrete or Abstract
}


const OppositeWords: React.FC = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const modal = useRef<HTMLIonModalElement>(null);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleSubmit = (option: React.SetStateAction<string | null>) => {
        setSelectedOption(option);
        const currentQuestion = questions[currentIndex];
        if (option === currentQuestion.correctAnswer) {
            // Automatically move to the next question
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                }
                else {
                    setCurrentIndex(0);
                    fetchQuestions();
                }
            }, 5000); // Delay reduced to 3 seconds
        }
    };

    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'OppositeWords.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/OppositeWords.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Opposites – LKG English Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content='Help LKG kids learn about opposites with fun and interactive activities on Mama’s Guidance. Strengthen vocabulary and understanding of contrasting words through engaging exercises.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Opposites</h1>

                                <p>
                                    Help your child build a strong vocabulary with this fun-filled <strong>Opposites worksheet</strong> designed for LKG learners. Children will explore word pairs like <b><em>big–small</em>, <em>hot–cold</em>, and <em>happy–sad</em></b> through colorful visuals and matching games.
                                </p>

                                <h2>🔁 What Are Opposites?</h2>
                                <p>
                                    Opposites are words that show <strong>completely different meanings</strong>. Understanding opposites helps children learn how to describe the world around them and expand their vocabulary.
                                </p>

                                <h2>🧩 What’s in the Worksheet?</h2>
                                <ul>
                                    <li>Picture matching of opposite word pairs</li>
                                    <li>Circle the opposite word</li>
                                    <li>Simple fill-in-the-blank exercises</li>
                                    <li>Fun, easy-to-understand illustrations</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Understand the concept of opposite words</li>
                                    <li>Recognize and match common opposites</li>
                                    <li>Improve word recognition and early grammar</li>
                                    <li>Enhance observational and cognitive skills</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Show your child a picture and ask them to describe what they see.</li>
                                    <li>Ask <strong>“What is the opposite of this?”</strong> and guide them to the correct answer.</li>
                                    <li>Use real-world examples: <strong>hot tea vs. cold juice, open door vs. closed door</strong>.</li>
                                    <li>Encourage speaking and writing the opposite words aloud or tracing them.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/english-language/this-that-these-those"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>This–That & These–Those</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/picture-composition"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Picture Composition</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why Opposites Matter in Early Learning</h2>
                                <p>
                                    Learning opposites improves children&apos;s <strong>understanding of contrast, meaning, and relationships between words</strong>. It builds their ability to compare and describe objects and situations clearly.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Opposites</h1>

                            <p>
                                Help your child build a strong vocabulary with this fun-filled <strong>Opposites worksheet</strong> designed for LKG learners. Children will explore word pairs like <b><em>big–small</em>, <em>hot–cold</em>, and <em>happy–sad</em></b> through colorful visuals and matching games.
                            </p>

                            <h2>🔁 What Are Opposites?</h2>
                            <p>
                                Opposites are words that show <strong>completely different meanings</strong>. Understanding opposites helps children learn how to describe the world around them and expand their vocabulary.
                            </p>

                            <h2>🧩 What’s in the Worksheet?</h2>
                            <ul>
                                <li>Picture matching of opposite word pairs</li>
                                <li>Circle the opposite word</li>
                                <li>Simple fill-in-the-blank exercises</li>
                                <li>Fun, easy-to-understand illustrations</li>
                            </ul>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Understand the concept of opposite words</li>
                                <li>Recognize and match common opposites</li>
                                <li>Improve word recognition and early grammar</li>
                                <li>Enhance observational and cognitive skills</li>
                            </ul>

                            <h2>👩‍🏫 How to Use the Worksheet</h2>
                            <ul>
                                <li>Show your child a picture and ask them to describe what they see.</li>
                                <li>Ask <strong>“What is the opposite of this?”</strong> and guide them to the correct answer.</li>
                                <li>Use real-world examples: <strong>hot tea vs. cold juice, open door vs. closed door</strong>.</li>
                                <li>Encourage speaking and writing the opposite words aloud or tracing them.</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/lkg/english-language/this-that-these-those"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>This–That & These–Those</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/english-language/rhyming-words"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Rhyming Words</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/english-language/picture-composition"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Picture Composition</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why Opposites Matter in Early Learning</h2>
                            <p>
                                Learning opposites improves children&apos;s <strong>understanding of contrast, meaning, and relationships between words</strong>. It builds their ability to compare and describe objects and situations clearly.
                            </p>
                        </article>
                        <h3>{questions[currentIndex]?.word}</h3>

                        <IonRadioGroup
                            value={selectedOption}
                            onIonChange={(e) => handleSubmit(e.detail.value)}
                        >
                            {questions[currentIndex]?.options.map((option, index) => (
                                <IonItem
                                    className={` ${selectedOption === option
                                        ? option === questions[currentIndex]?.correctAnswer
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}
                                    key={index}>
                                    <IonLabel>{option}</IonLabel>
                                    <IonRadio slot="start" value={option} />
                                </IonItem>
                            ))}
                        </IonRadioGroup>
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default OppositeWords;
